import { useEffect, useState } from "react";
import { Alert, DatePicker, Flex, Input, Modal, Select, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import type { InsertNorthmillIntegrationItemRequest } from "apiClients/base-generated";

const { Text } = Typography;
const { TextArea } = Input;

export type NorthmillStockImportGroupOption = {
  value: string;
  label: string;
};

export type IntegrationNorthmillStockImportModalProps = {
  open: boolean;
  onCancel: () => void;
  groupOptions: NorthmillStockImportGroupOption[];
  groupsLoading?: boolean;
  confirmLoading?: boolean;
  onImport: (items: InsertNorthmillIntegrationItemRequest[]) => void | Promise<void>;
};

/** En rad: `artikelnummer,saldo` (första kommatecknet skiljer fälten). Tomma rader ignoreras. Vid dubblett av artnr används sista raden. */
export function parseNorthmillStockImportText(
  text: string,
  northmillGroupId: string | null,
  lockedUntilIso: string,
): { items: InsertNorthmillIntegrationItemRequest[]; lineErrors: string[] } {
  const lineErrors: string[] = [];
  const byItem = new Map<string, InsertNorthmillIntegrationItemRequest>();
  const rawLines = text.split(/\r?\n/);

  for (let i = 0; i < rawLines.length; i++) {
    const lineNum = i + 1;
    const trimmed = rawLines[i].trim();
    if (!trimmed) continue;

    const comma = trimmed.indexOf(",");
    if (comma < 0) {
      lineErrors.push(`Rad ${lineNum}: använd formatet artnr,saldo (ett kommatecken mellan fälten).`);
      continue;
    }

    const itemNo = trimmed.slice(0, comma).trim();
    const stockRaw = trimmed
      .slice(comma + 1)
      .trim()
      .replace(/\s/g, "")
      .replace(",", ".");

    if (!itemNo) {
      lineErrors.push(`Rad ${lineNum}: artikelnummer saknas före kommatecknet.`);
      continue;
    }
    if (stockRaw === "") {
      lineErrors.push(`Rad ${lineNum}: saldo saknas efter kommatecknet.`);
      continue;
    }

    const stock = Number(stockRaw);
    if (!Number.isFinite(stock) || stock < 0 || !Number.isInteger(stock)) {
      lineErrors.push(`Rad ${lineNum}: saldo måste vara ett heltal ≥ 0.`);
      continue;
    }

    byItem.set(itemNo, {
      itemNo,
      northmillGroupId,
      northmillLockedQuantity: stock,
      lockedUntil: lockedUntilIso,
      isInactive: false,
      northmillAccountCodeId: null,
    });
  }

  return { items: [...byItem.values()], lineErrors };
}

export function IntegrationNorthmillStockImportModal({
  open,
  onCancel,
  groupOptions,
  groupsLoading = false,
  confirmLoading = false,
  onImport,
}: IntegrationNorthmillStockImportModalProps) {
  const [groupId, setGroupId] = useState<string | null>(null);
  const [lockDate, setLockDate] = useState<Dayjs | null>(() => dayjs().add(7, "day"));
  const [pasteText, setPasteText] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setGroupId(null);
      setLockDate(dayjs().add(7, "day"));
      setPasteText("");
      setLocalError(null);
    }
  }, [open]);

  const handleOk = async () => {
    setLocalError(null);
    if (!groupId?.trim()) {
      setLocalError("Välj en Northmill-grupp.");
      return;
    }
    if (!lockDate) {
      setLocalError("Välj låsdatum (synk pausad till och med).");
      return;
    }

    const lockedUntilIso = lockDate.endOf("day").toISOString();
    const { items, lineErrors } = parseNorthmillStockImportText(pasteText, groupId, lockedUntilIso);

    if (lineErrors.length) {
      setLocalError(lineErrors.slice(0, 12).join("\n") + (lineErrors.length > 12 ? "\n…" : ""));
      return;
    }
    if (!items.length) {
      setLocalError("Lägg in minst en rad med formatet artnr,saldo.");
      return;
    }

    try {
      await Promise.resolve(onImport(items));
      onCancel();
    } catch {
      /* Felmeddelande från anroparen */
    }
  };

  return (
    <Modal
      open={open}
      title="Massimport — lager & synkpaus"
      onCancel={onCancel}
      onOk={handleOk}
      okText="Importera"
      width={560}
      destroyOnClose
      confirmLoading={confirmLoading}
    >
      <Flex vertical gap={14}>
        <Text type="secondary" style={{ fontSize: 14, lineHeight: 1.5 }}>
          Varje rad: <Text code>artikelnummer,saldo</Text>, t.ex. <Text code>509658,7</Text>. Samma låsdatum och grupp
          används för alla. Befintliga synkrader uppdateras; nya skapas vid behov.
        </Text>

        <Flex vertical gap={6}>
          <Text strong>Northmill-grupp</Text>
          <Select
            showSearch
            allowClear={false}
            placeholder="Välj grupp"
            style={{ width: "100%" }}
            options={groupOptions}
            value={groupId ?? undefined}
            onChange={(v) => {
              setGroupId(v);
              setLocalError(null);
            }}
            loading={groupsLoading}
            optionFilterProp="label"
          />
        </Flex>

        <Flex vertical gap={6}>
          <Text strong>Synk pausad till och med</Text>
          <DatePicker
            value={lockDate}
            onChange={(d) => {
              setLockDate(d);
              setLocalError(null);
            }}
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            disabledDate={(current) => !!current && current < dayjs().startOf("day")}
          />
        </Flex>

        <Flex vertical gap={6}>
          <Text strong>Artiklar och saldo</Text>
          <TextArea
            value={pasteText}
            onChange={(e) => {
              setPasteText(e.target.value);
              setLocalError(null);
            }}
            placeholder={"509658,7\n123456,0"}
            rows={12}
            style={{ fontFamily: "monospace", fontSize: 13 }}
          />
        </Flex>

        {localError && (
          <Alert type="error" showIcon message="Kontrollera inmatningen" description={<pre style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: 12 }}>{localError}</pre>} />
        )}
      </Flex>
    </Modal>
  );
}
